import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetTitle,
} from 'src/@/components/ui/sheet';
import { useSelector, useDispatch } from 'react-redux';
import ChatWindow from './ChatWindow';
import { ChatModalContext } from './ChatModalContext';
import {
    setChatInfo,
    setLimparChatInfo,
    setMessagesWhatsapp,
    setMessagesArquivosWhatsapp,
} from 'src/redux/slices/chat';

// ----------------------------------------------------------------------

DrawerChatWhatsapp.propTypes = {
    onClose: PropTypes.func,
    chat: PropTypes.object,
    open: PropTypes.bool,
    source: PropTypes.string,
};

export default function DrawerChatWhatsapp({ chat, open, onClose, source }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('chat');
    const { chatInfo } = useSelector((state) => state.chat);
    const { config } = useSelector((state) => state.global);
    const { isUserStandby } = useSelector((state) => state.user);
    const [sidebarLeft, setSidebarLeft] = useState(false);

    function handleClose() {
        onClose();
        dispatch(setChatInfo(null));
        dispatch(setLimparChatInfo());
        dispatch(setMessagesWhatsapp([]));
        dispatch(setMessagesArquivosWhatsapp([]));
    }

    // O botão de voltar vive dentro do header do chat (ChatHeader), via contexto.
    const modalContext = useMemo(() => ({ isModal: true, onBack: handleClose }), [onClose]);

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <SheetContent
                side="bottom"
                hideCloseButton
                className="h-screen w-screen max-w-none p-0 border-none"
            >
                <SheetTitle className="sr-only">{chatInfo?.nomeCliente || 'Chat'}</SheetTitle>

                <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
                    {/* Em standby o ChatWindow não renderiza o header — mantém uma saída visível */}
                    {isUserStandby && (
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-neutral-800 flex-shrink-0">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleClose}
                                aria-label={t('common:buttons.back')}
                                className="h-8 w-8 rounded-lg text-gray-500 dark:text-neutral-400 hover:bg-gray-100 dark:hover:bg-neutral-800"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {t('common:buttons.back')}
                            </span>
                        </div>
                    )}

                    <div className="chat-drawer-fullscreen flex-1 min-h-0 overflow-hidden">
                        {chatInfo ? (
                            <ChatModalContext.Provider value={modalContext}>
                                <ChatWindow
                                    sidebarLeft={sidebarLeft}
                                    changeOpenSidebarLeft={() => setSidebarLeft(!sidebarLeft)}
                                    config={config}
                                    chatInfo={chatInfo}
                                    source={source}
                                    onCloseModal={handleClose}
                                />
                            </ChatModalContext.Provider>
                        ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center gap-3">
                                <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    Carregando conversa...
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
